import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
    CreateFaceBody,
    FaceScope,
    UpdateFaceBody,
} from '@/lib/api/grower-faces-api'
import { faceSettingsApi, growerFacesApi } from '@/lib/api/grower-faces-api'

export const growerFaceKeys = {
    all: ['grower-faces'] as const,
    list: (growerId: string) => [...growerFaceKeys.all, growerId] as const,
    settings: ['face-recognition-settings'] as const,
}

/** Danh sách người trong hộ đã đăng ký khuôn mặt. */
export function useGrowerFaces(growerId: string | undefined, scope: FaceScope) {
    return useQuery({
        queryKey: growerFaceKeys.list(growerId ?? ''),
        queryFn: async () => {
            const res = await growerFacesApi.list(growerId as string, scope)
            return res.data ?? []
        },
        enabled: !!growerId,
    })
}

/**
 * Mọi thao tác ghi đều làm mới danh sách của hộ đó: thêm/xoá ảnh làm đổi
 * photo_count và is_usable của người tương ứng, mà hai giá trị đó backend tính
 * chứ frontend không suy ra được.
 */
function useInvalidateFaces(growerId: string) {
    const queryClient = useQueryClient()
    return () =>
        queryClient.invalidateQueries({
            queryKey: growerFaceKeys.list(growerId),
        })
}

export function useCreateFace(growerId: string, scope: FaceScope) {
    const invalidate = useInvalidateFaces(growerId)
    return useMutation({
        mutationFn: async (body: CreateFaceBody) => {
            const res = await growerFacesApi.create(growerId, scope, body)
            return res.data
        },
        onSuccess: invalidate,
    })
}

export function useUpdateFace(growerId: string, scope: FaceScope) {
    const invalidate = useInvalidateFaces(growerId)
    return useMutation({
        mutationFn: async (vars: { faceId: string; body: UpdateFaceBody }) => {
            const res = await growerFacesApi.update(
                growerId,
                scope,
                vars.faceId,
                vars.body,
            )
            return res.data
        },
        onSuccess: invalidate,
    })
}

export function useDeleteFace(growerId: string, scope: FaceScope) {
    const invalidate = useInvalidateFaces(growerId)
    return useMutation({
        mutationFn: (faceId: string) =>
            growerFacesApi.remove(growerId, scope, faceId),
        onSuccess: invalidate,
    })
}

export function useAddFacePhoto(growerId: string, scope: FaceScope) {
    const invalidate = useInvalidateFaces(growerId)
    return useMutation({
        mutationFn: async (vars: { faceId: string; blob: Blob }) => {
            const res = await growerFacesApi.addPhoto(
                growerId,
                scope,
                vars.faceId,
                vars.blob,
            )
            return res.data
        },
        onSuccess: invalidate,
    })
}

/**
 * Thay TOÀN BỘ bộ ảnh của một người bằng bộ vừa chụp.
 *
 * Cả bộ đi trong một lời gọi chứ không gửi từng tấm: server phải trích vector
 * cho tất cả trước rồi mới được xoá ảnh cũ, nếu không thì tấm cuối bị từ chối
 * là người đó mất sạch dữ liệu đang dùng tốt.
 */
export function useReplaceFacePhotos(growerId: string, scope: FaceScope) {
    const invalidate = useInvalidateFaces(growerId)
    return useMutation({
        mutationFn: async (vars: { faceId: string; blobs: Blob[] }) => {
            const res = await growerFacesApi.replacePhotos(
                growerId,
                scope,
                vars.faceId,
                vars.blobs,
            )
            return res.data
        },
        onSuccess: invalidate,
    })
}

export function useDeleteFacePhoto(growerId: string, scope: FaceScope) {
    const invalidate = useInvalidateFaces(growerId)
    return useMutation({
        mutationFn: (vars: { faceId: string; photoId: string }) =>
            growerFacesApi.removePhoto(
                growerId,
                scope,
                vars.faceId,
                vars.photoId,
            ),
        onSuccess: invalidate,
    })
}

/**
 * Thử so khớp. Cố ý KHÔNG invalidate gì: ảnh thử không được lưu ở đâu cả, nên
 * không có dữ liệu nào đổi.
 */
export function useTestFaceMatch(growerId: string, scope: FaceScope) {
    return useMutation({
        mutationFn: async (blob: Blob) => {
            const res = await growerFacesApi.testMatch(growerId, scope, blob)
            return res.data
        },
    })
}

/** Ngưỡng kết luận, dùng chung mọi hộ. */
export function useFaceSettings() {
    return useQuery({
        queryKey: growerFaceKeys.settings,
        queryFn: async () => {
            const res = await faceSettingsApi.get()
            return res.data
        },
    })
}

export function useUpdateFaceSettings() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (matchThreshold: number) => {
            const res = await faceSettingsApi.update(matchThreshold)
            return res.data
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: growerFaceKeys.settings,
            }),
    })
}
